import { mapState } from 'vuex';
import FieldValidator from '@/views/calculators/services/fieldValidatorService';
import { HouseSettings } from '../../house/HouseSettings';
import ExistingAbProductsComponent from '../../components/ExistingABProductsComponent.vue';
import { nextTick, PropType } from 'vue';
import { CardBlock } from '@/cms/definitions/content-types';
import InsurancesProductEnum from '@/enums/InsurancesProductEnum';
import { Options, Vue } from 'vue-class-component';
import { Validator } from '../../services/ValidationService';
import { copyAddress, mapDarAddress, resetAddress } from '@/views/calculators/services/addressMappingService';
import { handleAddressPrefill, getCustomerReadonlyAge } from '../../services/UiStepService';

@Options({
	name: 'PersonInfoStepComponent',
	props: {
		card: Object as PropType<CardBlock>, // Card from cms
		isValid: Boolean, // whether card is complete
	},
	components: {
		ExistingAbProductsComponent,
	},
	computed: mapState<any>({
		model: (state) => state.calculatorContext[state.calculatorContext.active].model,
		cms: (state) => state.calculatorContext[state.calculatorContext.active].cms,
	}),
})
export default class PersonInfoStepComponent extends Vue {
	card: CardBlock;
	isValid: boolean;

	public model!: any;
	public cms!: HouseSettings; // settings from CMS (store)

	public fieldValidator: FieldValidator = Validator;
	public defaultAddress: string = null;
	public readonlyAge: string = null;
	public holidayHouse = false;

	public created() {
		this.holidayHouse = this.model.productName === InsurancesProductEnum.FRITIDSHUS_FORSIKRING;

		const addressParam = this.$route.query.address as string;
		if (addressParam) {
			this.defaultAddress = addressParam;
		}
		const customerAgeParam = this.$route.query.customerAge as string;
		if (customerAgeParam) {
			this.model.personInfo.customerAge = customerAgeParam;
		}

		const almbrandCustomerParam = this.$route.query.almbrandCustomer as string;
		if (almbrandCustomerParam) {
			this.model.personInfo.almbrandCustomer = almbrandCustomerParam;
		}

		const existingAlmBrandProductsParam = this.$route.query.existingAlmBrandProducts as Array<string>;
		if (existingAlmBrandProductsParam) {
			this.model.personInfo.existingAlmBrandProducts = existingAlmBrandProductsParam;
		}
	}

	public mounted() {
		handleAddressPrefill(this.model.personInfo, this.cms);
		this.readonlyAge = getCustomerReadonlyAge(this.model);
		addEventListener('keyup', this.moveOn);
	}

	public beforeUnmount() {
		removeEventListener('keyup', this.moveOn);
	}

	public moveOn(evt) {
		if (evt.keyCode === 13 && this.isValid) {
			this.nextStep();
		}
	}

	public get validAddress(): boolean {
		if (this.holidayHouse && !this.model.personInfo.holidayAddress.address) {
			return false;
		}
		if (!this.holidayHouse && !this.model.personInfo.address) {
			return false;
		}
		return true;
	}

	public handleRadioClick(evt: any) {
		const name = evt.name ?? evt.id;
		this.model.personInfo[name] = evt.value;
	}

	public nextStep() {
		this.$emit('nextStep', this.card.routePath);
	}

	public async addressSearch(address) {
		if (address?.matchText) {
			if (this.holidayHouse) {
				await mapDarAddress(this.model.personInfo.holidayAddress, address);
			} else {
				await mapDarAddress(this.model.personInfo, address);
				if (!this.model.multipleProducts) {
					// also map the home Address if this is the only product
					copyAddress(this.model.personInfo, this.model.personInfo.homeAddress);
				}
			}

			this.model.pristineStep.set(this.card.name, true);
			nextTick(() => {
				// wait for transition to begin showing customerAge field
				if (this.model.personInfo.customerAge) {
					setTimeout(() => {
						(document.activeElement as any).blur();
					}, 300);
				} else {
					setTimeout(() => {
						const input = document.getElementById('customerAge');
						if (input) {
							input.focus();
						}
					}, 300);
				}
			});

			return;
		}
		if (this.holidayHouse) {
			resetAddress(this.model.personInfo.holidayAddress);
		} else {
			resetAddress(this.model.personInfo);
			resetAddress(this.model.personInfo.homeAddress);
		}
	}
}
